import { getDisplayVariantTitle, sanitizeProductTitleForDisplay } from './variantTitle';

const TITLE_VARIANT_SEPARATOR = ' — ';
const TITLE_VARIANT_SUFFIX_SEPARATORS = [' — ', ' - ', ' – '];

const COMMON_VARIANT_WORDS = new Set([
  'black', 'white', 'red', 'blue', 'green', 'grey', 'gray', 'silver', 'gold', 'natural',
  'pink', 'orange', 'yellow', 'purple', 'brown', 'cream', 'chrome', 'copper', 'bronze',
  'small', 'medium', 'large', 'xl', 'xxl', 'xs', 'size'
]);

function endsWithIgnoreCase(haystack, needle) {
  return String(haystack || '').toLowerCase().endsWith(String(needle || '').toLowerCase());
}

function escapeRegExp(value) {
  return String(value || '').replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

function stripEmbeddedVariantFromTitle(title, variantTitle) {
  let baseTitle = String(title || '').trim();
  const variant = String(variantTitle || '').trim();
  if (!baseTitle || !variant) return baseTitle;

  for (const sep of TITLE_VARIANT_SUFFIX_SEPARATORS) {
    const suffix = `${sep}${variant}`;
    if (endsWithIgnoreCase(baseTitle, suffix)) {
      baseTitle = baseTitle.slice(0, -suffix.length).trim();
      break;
    }
  }

  const parenSuffix = new RegExp(`\\(\\s*${escapeRegExp(variant)}\\s*\\)$`, 'i');
  if (parenSuffix.test(baseTitle)) {
    baseTitle = baseTitle.replace(parenSuffix, '').trim();
  }

  return baseTitle;
}

function looksLikeProductSpecSuffix(value) {
  const suffix = String(value || '').trim();
  if (!suffix) return true;
  if (suffix.length > 40) return true;
  return /accuracy|max weight|weight:|dimensions?|size:|capacity:/i.test(suffix);
}

function looksLikeDimensionOrSizeParen(value) {
  const inner = String(value || '').trim();
  if (!inner) return true;
  return /^\d+(\.\d+)?\s*(mm|cm|m|kg|g|oz|ml|l)\b/i.test(inner)
    || /^\d+\s*(inch|in|")\b/i.test(inner);
}

function looksLikeVariantSuffix(value) {
  const suffix = String(value || '').trim();
  if (!suffix || suffix.length > 24) return false;
  if (looksLikeProductSpecSuffix(suffix)) return false;
  if (looksLikeDimensionOrSizeParen(suffix)) return false;
  if (/[:/\\]|\bfor\b|\bwith\b|\bcompatible\b|\bcompatibility\b/i.test(suffix)) return false;
  if (/\b(filament|portafilter|freestyle|version|series|model|kit|pack|carbon|drone)\b/i.test(suffix)) return false;

  const words = suffix.split(/\s+/).filter(Boolean);
  if (words.length > 3) return false;

  if (words.length === 1) {
    const word = words[0];
    const lower = word.toLowerCase();
    if (COMMON_VARIANT_WORDS.has(lower)) return true;
    if (/^\d+g$/i.test(word)) return true;
    return false;
  }

  const lastWord = words[words.length - 1].toLowerCase();
  return COMMON_VARIANT_WORDS.has(lastWord);
}

function findRightmostVariantSeparator(title) {
  const trimmed = String(title || '').trim();
  let bestIdx = -1;
  let bestSepLen = 0;

  for (const sep of TITLE_VARIANT_SUFFIX_SEPARATORS) {
    const idx = trimmed.lastIndexOf(sep);
    if (idx > bestIdx) {
      bestIdx = idx;
      bestSepLen = sep.length;
    }
  }

  if (bestIdx <= 0) return null;
  return {
    base: trimmed.slice(0, bestIdx).trim(),
    suffix: trimmed.slice(bestIdx + bestSepLen).trim()
  };
}

function tryParseParentheticalVariantFromTitle(title) {
  const trimmed = String(title || '').trim();
  const match = trimmed.match(/^(.+?)\s*\(([^)]+)\)\s*$/);
  if (!match) return { title: trimmed, variantTitle: null };

  const base = match[1].trim();
  const inner = match[2].trim();
  if (!base || !inner || looksLikeProductSpecSuffix(inner) || looksLikeDimensionOrSizeParen(inner)) {
    return { title: trimmed, variantTitle: null };
  }

  return { title: base, variantTitle: inner };
}

function tryParseTrailingVariantFromTitle(title) {
  const trimmed = String(title || '').trim();
  if (!trimmed) return { title: trimmed, variantTitle: null };

  const split = findRightmostVariantSeparator(trimmed);
  if (split && split.base && split.suffix && looksLikeVariantSuffix(split.suffix)) {
    return { title: split.base, variantTitle: split.suffix };
  }

  return tryParseParentheticalVariantFromTitle(trimmed);
}

function tokenSet(value) {
  return new Set(String(value || '').toLowerCase().match(/[a-z0-9]+/g) || []);
}

/**
 * True when every word of the variant already appears in the title.
 * CCC coffee (2026-08-29): title ends "- Upcoming Roast, 160g" while the
 * variant is "Upcoming Roast / 160g" — the separators differ, so the
 * suffix-strip misses it and the card printed the same words twice.
 * Word-level equality keeps this conservative ("Black" is not redundant
 * with "Blackbird").
 */
function variantIsRedundantWithTitle(title, variantTitle) {
  const variantTokens = tokenSet(variantTitle);
  if (variantTokens.size === 0) return false;
  const titleTokens = tokenSet(title);
  for (const token of variantTokens) {
    if (!titleTokens.has(token)) return false;
  }
  return true;
}

export function resolvePoItemParts(item = {}, options = {}) {
  const cacheProduct = options.cacheProduct || null;
  const sku = String(item?.sku || item?.variant_sku || cacheProduct?.sku || '').trim();
  let title = sanitizeProductTitleForDisplay(
    item?.title || item?.productName || item?.name || cacheProduct?.title || '',
    { sku }
  );

  let variantTitle = getDisplayVariantTitle(
    item?.variantTitle || cacheProduct?.variantTitle,
    title
  );

  if (variantTitle) {
    title = stripEmbeddedVariantFromTitle(title, variantTitle);
  } else {
    const parsed = tryParseTrailingVariantFromTitle(title);
    title = parsed.title;
    variantTitle = getDisplayVariantTitle(parsed.variantTitle, title);
  }

  const displayTitle = variantTitle && title && !variantIsRedundantWithTitle(title, variantTitle)
    ? `${title}${TITLE_VARIANT_SEPARATOR}${variantTitle}`
    : (title || variantTitle || sku || '—');

  return {
    sku,
    title: title || sku || '—',
    variantTitle,
    displayTitle
  };
}

export function resolvePoItemDisplayTitle(item = {}, options = {}) {
  return resolvePoItemParts(item, options).displayTitle;
}
